import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { authModule } from '../../api/modules/auth.module';
import { showToast } from '../../utils/toast';
import { ResetPasswordView } from './ResetPassword.view';

/**
 * Reset password, the container. Both routes: `/reset-password` with the token
 * in the query, and `/reset-password/:userId/:token` with it in the path.
 *
 * It owns the token, which API call the token shape implies, the toasts and the
 * three second redirect. Everything visual lives in `ResetPassword.view.jsx`,
 * which is pure, so the state explorer can mount all four states of the real
 * screen without any of this running.
 *
 * `ResetPassword.css` is deleted with this conversion. Nothing else imported
 * it. The hand-rolled `LoadingSpinner` calls go with it: `AuthSubmit` owns the
 * busy state and the validating screen is its own view state.
 */

export default function ResetPassword() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [validating, setValidating] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();

  // Get token from URL path or query parameters
  const pathToken = params.token;
  const pathUidb64 = params.userId;
  const queryToken = searchParams.get('token');
  const token = pathToken || queryToken;
  const uidb64 = pathUidb64;
  const email = searchParams.get('email') || location.state?.email;

  useEffect(() => {
    // Validate token when component mounts
    const validateToken = async () => {
      if (!token) {
        showToast.error('Invalid reset link. Please request a new password reset.');
        navigate('/forget-password');
        return;
      }

      try {
        // For now, we'll just check if token exists
        // In a real implementation, you might want to validate the token with the backend
        setTokenValid(true);
        setValidating(false);
      } catch (error) {
        console.error('Token validation error:', error);
        showToast.error('Invalid or expired reset link. Please request a new one.');
        navigate('/forget-password');
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setLoading(true);
    setSubmitting(true);

    try {
      let result;

      // Use the appropriate API method based on available parameters
      if (uidb64 && token) {
        // Use token-based reset for URL pattern /reset-password/:userId/:token
        result = await authModule.resetPasswordWithToken(uidb64, token, values.new_password);
      } else {
        result = await authModule.resetPassword({
          email: email,
          token: token,
          new_password: values.new_password,
        });
      }

      if (result.success) {
        setSuccess(true);
        showToast.success(result.message);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Password reset successful. Please login with your new password.',
            },
          });
        }, 3000);
      } else {
        showToast.error(result.message);
        if (result.message.toLowerCase().includes('password')) {
          setFieldError('new_password', result.message);
        }
      }
    } catch (error) {
      showToast.error('Something went wrong. Please try again.');
      console.error('Reset password error:', error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleRequestNewLink = () => {
    navigate('/forget-password');
  };

  // Same precedence the pre-conversion component used: validating wins, then
  // success, then the invalid-token screen, then the form.
  const status = validating ? 'validating' : success ? 'success' : !tokenValid ? 'invalid' : 'form';

  return (
    <ResetPasswordView
      status={status}
      email={email}
      onSubmit={handleSubmit}
      onBackToLogin={handleBackToLogin}
      onRequestNewLink={handleRequestNewLink}
      loading={loading}
    />
  );
}